import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Badge, Button, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getContactMessages, markMessageAsRead, deleteContactMessage } from '../../services/firebase/contactService';
import { getAllNotificationCounts } from '../../services/firebase/notificationService';

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentMessage, setCurrentMessage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if admin is logged in
    const isLoggedIn = localStorage.getItem('adminLoggedIn') === 'true';
    if (!isLoggedIn) {
      navigate('/admin/login');
      return;
    }
    
    // Load messages
    loadMessages();
  }, [navigate]);

  const loadMessages = async () => {
    try {
      const messagesData = await getContactMessages();
      setMessages(messagesData);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleViewMessage = async (message) => {
    setCurrentMessage(message);
    setShowModal(true);
    
    // Mark as read if it's unread
    if (!message.read) {
      try {
        await markMessageAsRead(message.id);
        await loadMessages(); // Refresh the list
        
        // Refresh notification counts
        try {
          await getAllNotificationCounts();
        } catch (error) {
          console.error('Error refreshing notification counts:', error);
        }
      } catch (error) {
        console.error('Error marking message as read:', error);
      }
    }
  };

  const handleDeleteMessage = async (id) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await deleteContactMessage(id);
        await loadMessages();
        
        // Refresh notification counts
        try {
          await getAllNotificationCounts();
        } catch (error) {
          console.error('Error refreshing notification counts:', error);
        }
      } catch (error) {
        console.error('Error deleting message:', error);
      }
    }
  };

  const getReadStatusBadge = (isRead) => {
    return isRead ? 
      <Badge bg="secondary">Read</Badge> : 
      <Badge bg="primary">New</Badge>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <section className="page-section">
      <Container fluid>
        <Row className="mb-4 align-items-center">
          <Col>
            <h2>Contact Messages</h2>
            <p>Manage messages from the contact form</p>
          </Col>
        </Row>
        
        <div className="bg-white p-4 shadow-sm rounded">
          <h3 className="mb-4">Messages</h3>
          
          {messages.length === 0 ? (
            <div className="text-center py-5">
              <p className="mb-0">No messages found.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((message) => (
                    <tr key={message.id} className={!message.read ? 'fw-bold' : ''}>
                      <td>{formatDate(message.createdAt)}</td>
                      <td>{message.name}</td>
                      <td>{message.email}</td>
                      <td>{message.subject}</td>
                      <td>{getReadStatusBadge(message.read)}</td>
                      <td>
                        <div className="d-flex">
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            className="me-2"
                            onClick={() => handleViewMessage(message)}
                          >
                            View
                          </Button>
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => handleDeleteMessage(message.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
        
        {/* Message View Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Message Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {currentMessage && (
              <>
                <p><strong>From:</strong> {currentMessage.name}</p>
                <p><strong>Email:</strong> {currentMessage.email}</p>
                <p><strong>Phone:</strong> {currentMessage.phone}</p>
                <p><strong>Subject:</strong> {currentMessage.subject}</p>
                <p><strong>Date:</strong> {formatDate(currentMessage.createdAt)}</p>
                <hr />
                <h5>Message:</h5>
                <p className="p-3 bg-light rounded">{currentMessage.message}</p>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
            {currentMessage && (
              <Button 
                variant="danger" 
                onClick={() => {
                  setShowModal(false);
                  handleDeleteMessage(currentMessage.id);
                }}
              >
                Delete Message
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </Container>
    </section>
  );
};

export default AdminMessages;